import React from 'react';

import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';

import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';

import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';

import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import {
  BieRecordId,
  IBieOnboardingWorkbenchImport,
  getBieImportClassLabel,
  isBieFlagEnabled,
} from './bieonboardingworkbench.models';

import BieOnboardingStatusChip from './BieOnboardingStatusChip';

/* ============================================================
 * Types
 * ============================================================
 */

export interface IBieOnboardingImportCardProps {
  /**
   * Import or worksheet represented by this card.
   */
  data: IBieOnboardingWorkbenchImport;

  /**
   * Shows the card in a selected state.
   */
  selected?: boolean;

  /**
   * Replaces the card content with loading placeholders.
   */
  loading?: boolean;

  /**
   * Disables interactions.
   */
  disabled?: boolean;

  /**
   * Enables the main card selection action.
   */
  clickable?: boolean;

  /**
   * Called when the main card body is selected.
   */
  onClick?: (
    importId: BieRecordId,
    data: IBieOnboardingWorkbenchImport
  ) => void;

  /**
   * Opens the issue list filtered to this Import.
   */
  onViewIssues?: (
    importId: BieRecordId,
    data: IBieOnboardingWorkbenchImport
  ) => void;

  /**
   * Opens the staged records belonging to this Import.
   */
  onViewRecords?: (
    importId: BieRecordId,
    data: IBieOnboardingWorkbenchImport
  ) => void;

  compact?: boolean;

  sx?: SxProps<Theme>;
}

/* ============================================================
 * Internal counter
 * ============================================================
 */

interface IBieImportCounterProps {
  label: string;
  value: number;
  icon: React.ReactElement;
  tone: 'default' | 'info' | 'success' | 'warning' | 'error';
}

/* ============================================================
 * Component
 * ============================================================
 */

/**
 * Displays one uploaded workbook sheet or onboarding Import.
 *
 * The card presents:
 *
 * - Import class and source worksheet
 * - Filename and upload metadata
 * - Total, pending, valid, warning and failed rows
 * - Validation status
 * - Issue and unresolved-reference counts
 * - Readiness for the next workflow action
 */
export default class BieOnboardingImportCard extends React.PureComponent<IBieOnboardingImportCardProps> {
  public render(): React.ReactNode {
    const {
      loading = false,
      clickable = true,
      disabled = false,
      selected = false,
      sx,
    } = this.props;

    return (
      <Card
        variant="outlined"
        sx={{
          height: '100%',

          position: 'relative',
          overflow: 'hidden',

          borderWidth: selected ? 2 : 1,

          borderColor: (theme: Theme) =>
            selected ? theme.palette.primary.main : theme.palette.divider,

          opacity: disabled ? 0.65 : 1,

          transition:
            'border-color 160ms ease, ' +
            'box-shadow 160ms ease, ' +
            'transform 160ms ease',

          '&::before': {
            content: '""',

            position: 'absolute',
            top: 0,
            left: 0,

            width: 4,
            height: '100%',

            backgroundColor: (theme: Theme) => this.getAccentColor(theme),
          },

          ...(clickable && !disabled && !loading
            ? {
                '&:hover': {
                  transform: 'translateY(-1px)',

                  boxShadow: (theme: Theme) => theme.shadows[3],
                },
              }
            : {}),

          ...sx,
        }}
      >
        {loading ? (
          this.renderLoading()
        ) : clickable ? (
          <CardActionArea
            disabled={disabled}
            onClick={this.handleCardClick}
            sx={{
              height: '100%',
              alignItems: 'stretch',
            }}
          >
            {this.renderContent()}
          </CardActionArea>
        ) : (
          this.renderContent()
        )}
      </Card>
    );
  }

  /* ==========================================================
   * Main content
   * ==========================================================
   */

  private renderContent(): React.ReactNode {
    const { data, compact = false } = this.props;

    const validPercentage = this.getValidPercentage();

    return (
      <CardContent
        sx={{
          height: '100%',

          p: compact ? 1.75 : 2.25,

          '&:last-child': {
            pb: compact ? 1.75 : 2.25,
          },
        }}
      >
        <Stack
          spacing={compact ? 1.25 : 1.75}
          sx={{
            height: '100%',
          }}
        >
          {this.renderHeader()}

          <Divider />

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {this.renderCounter({
              label: 'Total',
              value: data.counts.total,
              icon: <ArticleOutlinedIcon fontSize="small" />,
              tone: 'default',
            })}

            {this.renderCounter({
              label: 'Pending',
              value: data.counts.pending,
              icon: <HourglassEmptyOutlinedIcon fontSize="small" />,
              tone: 'info',
            })}

            {this.renderCounter({
              label: 'Valid',
              value: data.counts.valid,
              icon: <CheckCircleOutlineIcon fontSize="small" />,
              tone: 'success',
            })}

            {this.renderCounter({
              label: 'Warning',
              value: data.counts.warning,
              icon: <WarningAmberOutlinedIcon fontSize="small" />,
              tone: 'warning',
            })}

            {this.renderCounter({
              label: 'Failed',
              value: data.counts.failed,
              icon: <ErrorOutlineIcon fontSize="small" />,
              tone: 'error',
            })}
          </Stack>

          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{
                mb: 0.75,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Valid records
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                {validPercentage}%
              </Typography>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={validPercentage}
              color={
                data.counts.failed > 0
                  ? 'error'
                  : data.counts.warning > 0
                    ? 'warning'
                    : data.counts.pending > 0
                      ? 'info'
                      : 'success'
              }
              sx={{
                height: 7,
                borderRadius: 4,
              }}
            />
          </Box>

          {this.renderResolutionAndIssues()}

          <Box
            sx={{
              mt: 'auto',
            }}
          >
            <Divider
              sx={{
                mb: 1.5,
              }}
            />

            {this.renderActions()}
          </Box>
        </Stack>
      </CardContent>
    );
  }

  /* ==========================================================
   * Header
   * ==========================================================
   */

  private renderHeader(): React.ReactNode {
    const { data } = this.props;

    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Stack
          direction="row"
          spacing={1.25}
          sx={{
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,

              flexShrink: 0,

              borderRadius: 2,

              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',

              color: (theme: Theme) => this.getAccentColor(theme),

              backgroundColor: (theme: Theme) =>
                this.getAccentColor(theme) + '14',
            }}
          >
            <FolderOpenOutlinedIcon />
          </Box>

          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              noWrap
              title={data.name}
            >
              {data.name || getBieImportClassLabel(data.classCode)}
            </Typography>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{
                mt: 0.5,
              }}
            >
              <Chip
                size="small"
                variant="outlined"
                label={getBieImportClassLabel(data.classCode)}
                sx={{
                  fontWeight: 600,
                }}
              />

              <Tooltip title="Source workbook worksheet" arrow>
                <Chip
                  size="small"
                  label={data.sheetName || 'Unknown Sheet'}
                  icon={<ArticleOutlinedIcon />}
                />
              </Tooltip>
            </Stack>
          </Box>
        </Stack>

        <Stack
          spacing={0.75}
          alignItems="flex-end"
          sx={{
            flexShrink: 0,
          }}
        >
          <BieOnboardingStatusChip
            kind="VALIDATION"
            value={data.validationStatus}
          />

          <BieOnboardingStatusChip
            kind="READINESS"
            value={data.validationReady}
          />
        </Stack>
      </Stack>
    );
  }

  /* ==========================================================
   * Counters
   * ==========================================================
   */

  private renderCounter(counter: IBieImportCounterProps): React.ReactNode {
    return (
      <Box
        key={counter.label}
        sx={{
          minWidth: 86,

          px: 1.1,
          py: 0.85,

          borderRadius: 1.5,

          border: (theme: Theme) =>
            `1px solid ${this.getCounterColor(theme, counter.tone)}`,

          backgroundColor: (theme: Theme) =>
            this.getCounterColor(theme, counter.tone) + '0C',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box
            sx={{
              display: 'flex',

              color: (theme: Theme) =>
                this.getCounterColor(theme, counter.tone),
            }}
          >
            {counter.icon}
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={700} lineHeight={1.1}>
              {this.formatNumber(counter.value)}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {counter.label}
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  }

  /* ==========================================================
   * Issues and reference resolution
   * ==========================================================
   */

  private renderResolutionAndIssues(): React.ReactNode {
    const { data } = this.props;

    return (
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        spacing={1.25}
      >
        <Box
          sx={{
            flex: 1,

            px: 1.25,
            py: 1,

            borderRadius: 1.5,
            backgroundColor: 'action.hover',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <RuleOutlinedIcon
              fontSize="small"
              color={
                data.issues.blocking > 0
                  ? 'error'
                  : data.issues.warning > 0
                    ? 'warning'
                    : 'action'
              }
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Validation issues
              </Typography>

              <Typography variant="body2" fontWeight={700}>
                {this.formatNumber(data.issues.total)}
                {' total · '}
                {this.formatNumber(data.issues.blocking)}
                {' blocking'}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,

            px: 1.25,
            py: 1,

            borderRadius: 1.5,
            backgroundColor: 'action.hover',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <LinkOffOutlinedIcon
              fontSize="small"
              color={
                data.resolution.unresolvedReferences > 0 ? 'warning' : 'action'
              }
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Reference resolution
              </Typography>

              <Typography variant="body2" fontWeight={700}>
                {this.formatNumber(data.resolution.unresolvedReferences)}
                {' unresolved'}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Stack>
    );
  }

  /* ==========================================================
   * Actions
   * ==========================================================
   */

  private renderActions(): React.ReactNode {
    const { data, disabled = false, onViewIssues, onViewRecords } = this.props;

    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
      >
        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Tooltip title={data.filename || ''} arrow>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{
                display: 'block',
                maxWidth: 220,
              }}
            >
              {data.filename || 'Workbook filename unavailable'}
            </Typography>
          </Tooltip>

          {data.workbookVersion && (
            <Typography variant="caption" color="text.disabled">
              Version {data.workbookVersion}
            </Typography>
          )}
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexShrink: 0,
          }}
        >
          {onViewIssues && (
            <Button
              size="small"
              variant={data.issues.total > 0 ? 'outlined' : 'text'}
              color={
                data.issues.blocking > 0
                  ? 'error'
                  : data.issues.warning > 0
                    ? 'warning'
                    : 'inherit'
              }
              disabled={disabled}
              startIcon={
                data.issues.total > 0 ? (
                  <ReportProblemOutlinedIcon />
                ) : (
                  <RuleOutlinedIcon />
                )
              }
              onClick={this.handleViewIssues}
            >
              Issues
              {data.issues.total > 0
                ? ` (${this.formatNumber(data.issues.total)})`
                : ''}
            </Button>
          )}

          {onViewRecords && (
            <Button
              size="small"
              variant="text"
              disabled={disabled}
              startIcon={<VisibilityOutlinedIcon />}
              onClick={this.handleViewRecords}
            >
              Records
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }

  /* ==========================================================
   * Loading state
   * ==========================================================
   */

  private renderLoading(): React.ReactNode {
    return (
      <CardContent
        sx={{
          p: 2.25,

          '&:last-child': {
            pb: 2.25,
          },
        }}
      >
        <Stack spacing={1.75}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Stack
              direction="row"
              spacing={1.25}
              sx={{
                flex: 1,
              }}
            >
              <Skeleton variant="rounded" width={40} height={40} />

              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Skeleton variant="text" width="70%" height={28} />

                <Skeleton variant="text" width="45%" height={22} />
              </Box>
            </Stack>

            <Skeleton variant="rounded" width={92} height={28} />
          </Stack>

          <Divider />

          <Stack direction="row" spacing={1}>
            {[1, 2, 3, 4].map((value) => (
              <Skeleton key={value} variant="rounded" width={86} height={54} />
            ))}
          </Stack>

          <Skeleton variant="rounded" width="100%" height={8} />

          <Stack direction="row" spacing={1.25}>
            <Skeleton variant="rounded" width="50%" height={62} />

            <Skeleton variant="rounded" width="50%" height={62} />
          </Stack>
        </Stack>
      </CardContent>
    );
  }

  /* ==========================================================
   * Event handlers
   * ==========================================================
   */

  private readonly handleCardClick = (): void => {
    const { data, disabled, onClick } = this.props;

    if (disabled || !onClick) {
      return;
    }

    onClick(data.rid, data);
  };

  private readonly handleViewIssues = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    event.preventDefault();
    event.stopPropagation();

    const { data, disabled, onViewIssues } = this.props;

    if (disabled || !onViewIssues) {
      return;
    }

    onViewIssues(data.rid, data);
  };

  private readonly handleViewRecords = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    event.preventDefault();
    event.stopPropagation();

    const { data, disabled, onViewRecords } = this.props;

    if (disabled || !onViewRecords) {
      return;
    }

    onViewRecords(data.rid, data);
  };

  /* ==========================================================
   * Helpers
   * ==========================================================
   */

  private getValidPercentage(): number {
    const { counts } = this.props.data;

    const total = Number(counts.total) || 0;

    const valid = Number(counts.valid) || 0;

    if (total <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(100, Math.round((valid / total) * 100)));
  }

  private getAccentColor(theme: Theme): string {
    const { data } = this.props;

    if (data.counts.failed > 0 || data.issues.blocking > 0) {
      return theme.palette.error.main;
    }

    if (
      data.counts.warning > 0 ||
      data.issues.warning > 0 ||
      data.resolution.unresolvedReferences > 0
    ) {
      return theme.palette.warning.main;
    }

    if (data.counts.pending > 0) {
      return theme.palette.info.main;
    }

    if (isBieFlagEnabled(data.validationReady)) {
      return theme.palette.success.main;
    }

    return theme.palette.primary.main;
  }

  private getCounterColor(
    theme: Theme,
    tone: 'default' | 'info' | 'success' | 'warning' | 'error'
  ): string {
    switch (tone) {
      case 'info':
        return theme.palette.info.main;

      case 'success':
        return theme.palette.success.main;

      case 'warning':
        return theme.palette.warning.main;

      case 'error':
        return theme.palette.error.main;

      case 'default':
      default:
        return theme.palette.divider;
    }
  }

  private formatNumber(value: unknown): string {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return '0';
    }

    return new Intl.NumberFormat().format(numericValue);
  }
}
